import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, Tag, Spin, Alert, Empty, Tabs, Statistic, Col, message, Select } from 'antd';
import { 
  TrophyOutlined, 
  ClockCircleOutlined, 
  CheckCircleOutlined,
  ReloadOutlined,
  WifiOutlined,
  WifiOutlined as WifiOffOutlined
} from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { useWebSocket, WebSocketMessage } from '@/hooks/useWebSocket';
import { TournamentService } from '@/services/tournament.service';
import { Match } from '@/types/api.types';
import { useAuth } from '@/contexts/AuthContext';
import { logger } from '@/utils/logger';
import { api } from '@/api';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import {
  ScoreboardContainer,
  MatchesContainer,
  MatchCard,
  ParticipantRow,
  ParticipantInfo,
  ParticipantName,
  ParticipantDetails,
  ScoreDisplay,
  MatchInfo,
  TimerDisplay,
  StatusBadge,
  HeaderSection,
  ConnectionStatus,
  StatsRow,
  WinnerButton
} from './styles/LiveScoreboard.styles';

dayjs.extend(duration);

export const LiveScoreboard: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const tournamentId = id ? Number(id) : 0;

  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [matchTimers, setMatchTimers] = useState<Record<number, { startTime: Date; elapsed: number }>>({});
  const [activeTab, setActiveTab] = useState<'live' | 'completed'>('live');
  const [settingWinner, setSettingWinner] = useState<number | null>(null);
  const [selectedTatami, setSelectedTatami] = useState<number | 'all'>('all');
  const [tournament, setTournament] = useState<any>(null);

  // Проверка прав организатора
  const isUserOrganizer = user?.role === 'admin' || user?.role === 'manager';
  
  // Проверяем, опубликованы ли сетки
  const bracketsPublished = tournament?.bracketsPublished ?? false;
  
  // Проверяем, может ли пользователь видеть обновления в реальном времени
  const isAdmin = user?.role === 'admin';
  const isOrganizer = tournament && user && tournament.organizerId === user.id;
  const isManagerOrCoach = user && (user.role === 'manager' || user.role === 'head_manager' || user.role === 'coach');
  const isTournamentClubMember = tournament && user && tournament.clubId && user.clubId && tournament.clubId === user.clubId;
  const canViewUpdates = isAdmin || isOrganizer || (isManagerOrCoach && isTournamentClubMember) || bracketsPublished;

  // Загрузка данных турнира для проверки bracketsPublished
  useEffect(() => {
    const fetchTournament = async () => {
      if (!tournamentId) return;
      try {
        const tournamentData = await TournamentService.getById(tournamentId);
        setTournament(tournamentData);
      } catch (err) {
        logger.error('Error fetching tournament:', err);
      }
    };
    fetchTournament();
  }, [tournamentId]);

  // WebSocket подключение (только если можно видеть обновления)
  const { isConnected, reconnect } = useWebSocket({
    tournamentId,
    userId: user?.id,
    role: user?.role,
    enabled: canViewUpdates, // Отключаем WebSocket, если нельзя видеть обновления
    onMessage: (message: WebSocketMessage) => {
      
      // Дополнительная проверка на случай, если WebSocket все еще подключен
      if (!canViewUpdates) return;
      
      switch (message.type) {
        case 'match_started':
          handleMatchStarted(message.data);
          break;
        case 'match_updated':
        case 'score_updated':
          handleMatchUpdated(message.data);
          break;
        case 'match_completed':
          handleMatchCompleted(message.data);
          break;
        case 'bracket_generated':
          // Перезагружаем матчи при генерации сетки
          fetchMatches();
          break;
      }
    },
    onError: (err) => {
      // Игнорируем ошибки Vite HMR WebSocket
      const errorEvent = err as any;
      const isViteHMR = errorEvent?.isTrusted === true && 
                       (!errorEvent?.target?.url || 
                        errorEvent.target.url.includes('localhost:3000') || 
                        errorEvent.target.url.includes('vite'));
      
      if (!isViteHMR) {
        logger.error('WebSocket error:', err);
        setError('Ошибка подключения к серверу');
      }
    },
    onConnect: () => {
      logger.info('WebSocket connected');
      setError(null);
    },
    onDisconnect: () => {
      logger.warn('WebSocket disconnected');
    },
    autoReconnect: true,
  });

  // Нормализация данных матча с бэкенда
  const normalizeMatch = (match: any): Match => {
    // Обрабатываем разные варианты названий полей
    const normalized: any = {
      id: match.id,
      tournamentId: match.tournamentId,
      categoryId: match.categoryId,
      participant1Id: match.participant1Id,
      participant2Id: match.participant2Id,
      winnerParticipantId: match.winnerParticipantId || match.winnerId || (match.winner?.id),
      participant1Score: match.participant1Score || match.score1 || 0,
      participant2Score: match.participant2Score || match.score2 || 0,
      round: match.round,
      position: match.position,
      matchNumber: match.matchNumber,
      nextMatchNumber: match.nextMatchNumber,
      status: match.status || 'pending',
      tatamiNumber: match.tatamiNumber,
      startTime: match.startTime,
      createdAt: match.createdAt,
      updatedAt: match.updatedAt,
    };

    // Обрабатываем участников
    if (match.participant1) {
      normalized.participant1 = typeof match.participant1 === 'string' 
        ? match.participant1 
        : {
            id: match.participant1.id || match.participant1Id,
            firstName: match.participant1.firstName || match.participant1.first_name || '',
            lastName: match.participant1.lastName || match.participant1.last_name || '',
            club: match.participant1.club || match.participant1.user?.club || match.participant1.clubName || match.participant1.user?.clubName,
            city: match.participant1.city || match.participant1.user?.city || match.participant1.club?.city || match.participant1.user?.club?.city,
            user: match.participant1.user,
          };
    }

    if (match.participant2) {
      normalized.participant2 = typeof match.participant2 === 'string' 
        ? match.participant2 
        : {
            id: match.participant2.id || match.participant2Id,
            firstName: match.participant2.firstName || match.participant2.first_name || '',
            lastName: match.participant2.lastName || match.participant2.last_name || '',
            club: match.participant2.club || match.participant2.user?.club || match.participant2.clubName || match.participant2.user?.clubName,
            city: match.participant2.city || match.participant2.user?.city || match.participant2.club?.city || match.participant2.user?.club?.city,
            user: match.participant2.user,
          };
    }

    // Обрабатываем категорию (может быть вложенной через weightCategory)
    if (match.category) {
      if (typeof match.category === 'string') {
        normalized.category = { id: match.categoryId || 0, name: match.category };
      } else if (match.category.weightCategory) {
        // Вложенная структура: category.weightCategory.name
        normalized.category = {
          id: match.category.weightCategory.id || match.categoryId || 0,
          name: match.category.weightCategory.name || '',
        };
      } else {
        normalized.category = {
          id: match.category.id || match.categoryId || 0,
          name: match.category.name || '',
        };
      }
    }

    return normalized as Match;
  };

  // Загрузка матчей с поддержкой отмены
  const fetchMatches = useCallback(async (signal?: AbortSignal) => {
    if (!tournamentId) return;

    try {
      setLoading(true);
      setError(null);
      const matchesData = await TournamentService.getTournamentMatches(tournamentId);
      
      // Проверяем, не был ли запрос отменен
      if (signal?.aborted) {
        return;
      }
      
      // Обрабатываем разные форматы ответа
      let rawMatches: any[] = [];
      if (Array.isArray(matchesData)) {
        rawMatches = matchesData;
      } else if ((matchesData as any)?.data && Array.isArray((matchesData as any).data)) {
        rawMatches = (matchesData as any).data;
      } else if ((matchesData as any)?.success && (matchesData as any)?.data) {
        rawMatches = (matchesData as any).data;
      }
      
      // Проверяем еще раз, не был ли запрос отменен
      if (signal?.aborted) {
        return;
      }
      
      // Нормализуем каждый матч
      const normalizedMatches = rawMatches.map(normalizeMatch);
            
      setMatches(normalizedMatches);
      
      // Инициализируем таймеры для матчей в процессе
      const timers: Record<number, { startTime: Date; elapsed: number }> = {};
      normalizedMatches.forEach((match: Match) => {
        if (match.status === 'in_progress' && match.createdAt) {
          const startTime = new Date(match.createdAt);
          const elapsed = Date.now() - startTime.getTime();
          timers[match.id] = { startTime, elapsed };
        }
      });
      setMatchTimers(timers);
      
    } catch (err: any) {
      logger.error('Error fetching matches:', err);
      setError(err.message || 'Ошибка при загрузке матчей');
    } finally {
      setLoading(false);
    }
  }, [tournamentId]);

  // Обработка начала матча
  const handleMatchStarted = (data: any) => {
    if (data?.matchId) {
      setMatches(prev => {
        const existingMatch = prev.find(m => m.id === data.matchId);
        
        if (existingMatch) {
          // Обновляем существующий матч
          return prev.map(m => {
            if (m.id === data.matchId) {
              const updated = data.match 
                ? normalizeMatch({ ...m, ...data.match, status: 'in_progress' })
                : { ...m, status: 'in_progress' as const };
              return updated;
            }
            return m;
          });
        } else if (data.match) {
          // Добавляем новый матч
          const normalized = normalizeMatch({ ...data.match, status: 'in_progress' });
          return [...prev, normalized];
        }
        
        return prev;
      });

      // Запускаем таймер
      const startTime = data.startTime 
        ? new Date(data.startTime) 
        : new Date();
      setMatchTimers(prev => ({
        ...prev,
        [data.matchId]: { startTime, elapsed: 0 }
      }));
    }
  };

  // Обработка обновления матча
  const handleMatchUpdated = (data: any) => {
    if (data?.matchId) {
      setMatches(prev => prev.map(m => {
        if (m.id === data.matchId) {
          const updated = { ...m };
          
          // Обновляем данные из data.match если есть
          if (data.match) {
            const normalized = normalizeMatch({ ...m, ...data.match });
            return normalized;
          }
          
          // Иначе обновляем только отдельные поля
          if (data.participant1Score !== undefined) {
            updated.participant1Score = data.participant1Score;
          }
          if (data.participant2Score !== undefined) {
            updated.participant2Score = data.participant2Score;
          }
          if (data.winnerId !== undefined || data.winnerParticipantId !== undefined) {
            updated.winnerParticipantId = data.winnerId || data.winnerParticipantId;
          }
          if (data.status) {
            updated.status = data.status;
          }
          
          return updated;
        }
        return m;
      }));
    }
  };

  // Обработка завершения матча
  const handleMatchCompleted = (data: any) => {
    if (data?.matchId) {
      setMatches(prev => prev.map(m => {
        if (m.id === data.matchId) {
          const updated = {
            ...m,
            status: 'completed' as const,
            winnerParticipantId: data.winnerId ?? m.winnerParticipantId,
            participant1Score: data.finalScore?.participant1 ?? m.participant1Score,
            participant2Score: data.finalScore?.participant2 ?? m.participant2Score,
          };
          return updated;
        }
        return m;
      }));

      // Останавливаем таймер
      setMatchTimers(prev => {
        const newTimers = { ...prev };
        delete newTimers[data.matchId];
        return newTimers;
      });
    }
  };

  // Обновление таймеров каждую секунду
  useEffect(() => {
    const interval = setInterval(() => {
      setMatchTimers(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(matchId => {
          const timer = updated[Number(matchId)];
          if (timer) {
            updated[Number(matchId)] = {
              ...timer,
              elapsed: Date.now() - timer.startTime.getTime()
            };
          }
        });
        return updated;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Загрузка матчей при монтировании с поддержкой отмены
  useEffect(() => {
    if (!tournamentId) return;
    
    const abortController = new AbortController();
    
    fetchMatches(abortController.signal);
    
    return () => {
      abortController.abort();
    };
  }, [tournamentId, fetchMatches]);

  // Автоматическое обновление для неавторизованных пользователей (каждую минуту)
  useEffect(() => {
    if (!user && tournamentId) {
      const abortController = new AbortController();
      
      const interval = setInterval(() => {
        fetchMatches(abortController.signal);
      }, 60000); // 60 секунд = 1 минута

      return () => {
        clearInterval(interval);
        abortController.abort();
      };
    }
  }, [user, tournamentId, fetchMatches]);

  // Получение списка всех уникальных татами
  const availableTatami = useMemo(() => {
    const tatamiSet = new Set<number>();
    matches.forEach(match => {
      if (match.tatamiNumber) {
        tatamiSet.add(match.tatamiNumber);
      }
    });
    return Array.from(tatamiSet).sort((a, b) => a - b);
  }, [matches]);

  // Разделение матчей по статусам с фильтрацией по татами
  // Все не завершенные матчи (pending и in_progress) идут во вкладку "В процессе"
  const { liveMatches, completedMatches } = useMemo(() => {
    // Фильтруем по выбранному татами
    const filteredMatches = selectedTatami === 'all' 
      ? matches 
      : matches.filter(m => m.tatamiNumber === selectedTatami);
    
    const live = filteredMatches.filter(m => m.status !== 'completed' && m.status !== 'cancelled');
    const completed = filteredMatches.filter(m => m.status === 'completed');
    
    return {
      liveMatches: live.sort((a, b) => {
        // Сначала сортируем по статусу (in_progress выше pending)
        if (a.status === 'in_progress' && b.status === 'pending') return -1;
        if (a.status === 'pending' && b.status === 'in_progress') return 1;
        // Затем по номеру матча (по возрастанию)
        return (a.matchNumber || 0) - (b.matchNumber || 0);
      }),
      completedMatches: completed.sort((a, b) => (a.matchNumber || 0) - (b.matchNumber || 0)),
    };
  }, [matches, selectedTatami]);

  // Форматирование времени
  const formatTime = (elapsed: number): string => {
    const duration = dayjs.duration(elapsed);
    const minutes = Math.floor(duration.asMinutes());
    const seconds = duration.seconds();
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  // Получение информации о клубе и городе участника
  const getParticipantInfo = (participant: any): { name: string; club?: string; city?: string } => {
    if (!participant) return { name: 'TBD' };
    if (typeof participant === 'string') return { name: participant };
    
    const name = participant.firstName && participant.lastName
      ? `${participant.firstName} ${participant.lastName}`
      : 'Неизвестный участник';
    
    // Получаем информацию о клубе
    const club = participant.club?.name 
      || participant.clubName 
      || participant.user?.club?.name
      || participant.user?.clubName
      || (participant.club && typeof participant.club === 'string' ? participant.club : undefined);
    
    // Получаем информацию о городе
    const city = participant.city
      || participant.club?.city
      || participant.user?.city
      || participant.user?.club?.city
      || undefined;
    
    return { name, club, city };
  };

  // Установка победителя матча
  const handleSetWinner = async (match: Match, winnerId: number) => {
    if (!match.id) {
      message.error('Не удалось определить матч');
      return;
    }

    try {
      setSettingWinner(match.id);
      await api.put(`/tournaments/${tournamentId}/matches/${match.id}/result`, {
        winnerId
      });

      message.success('Победитель установлен');
      // Перезагружаем матчи для обновления данных
      await fetchMatches();
    } catch (error: any) {
      logger.error('Error setting winner:', error);
      message.error(error?.response?.data?.message || 'Ошибка при установке победителя');
    } finally {
      setSettingWinner(null);
    }
  };

  // Рендер карточки матча
  const renderMatchCard = (match: Match) => {
    const isLive = match.status === 'in_progress';
    const isCompleted = match.status === 'completed';
    const timer = matchTimers[match.id];
    const participant1Info = getParticipantInfo(match.participant1);
    const participant2Info = getParticipantInfo(match.participant2);
    const isWinner1 = match.winnerParticipantId === match.participant1Id;
    const isWinner2 = match.winnerParticipantId === match.participant2Id;
    
    // Проверяем, есть ли только один участник (bye)
    const hasOnlyOneParticipant = !match.participant1Id || !match.participant2Id;

    return (
      <MatchCard 
        key={match.id}
        $isActive={isLive}
        $isCompleted={isCompleted}
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontWeight: 'bold' }}>
                {match.matchNumber ? `Бой #${match.matchNumber}` : `Бой #${match.id}`}
              </span>
              {match.category && (
                <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal' }}>
                  {match.category.name}
                </span>
              )}
            </div>
            <Tag 
              color={isLive ? 'processing' : isCompleted ? 'success' : 'default'}
              style={{ 
                fontSize: isLive || isCompleted ? '14px' : '16px',
                fontWeight: isLive || isCompleted ? 'normal' : 'bold',
                padding: isLive || isCompleted ? '4px 8px' : '6px 12px'
              }}
            >
              {isLive ? 'В процессе' : isCompleted ? 'Завершен' : 'Ожидание'}
            </Tag>
          </div>
        }
        extra={
          isLive && timer && (
            <TimerDisplay>
              <ClockCircleOutlined />
              {formatTime(timer.elapsed)}
            </TimerDisplay>
          )
        }
      >
        {hasOnlyOneParticipant ? (
          <ParticipantRow $isWinner={false}>
            <ParticipantInfo>
              <ParticipantName>{participant1Info.name || participant2Info.name}</ParticipantName>
              {(participant1Info.club || participant1Info.city || participant2Info.club || participant2Info.city) && (
                <ParticipantDetails>
                  {participant1Info.club || participant2Info.club}
                  {(participant1Info.club || participant2Info.club) && (participant1Info.city || participant2Info.city) && ' • '}
                  {participant1Info.city || participant2Info.city}
                </ParticipantDetails>
              )}
            </ParticipantInfo>
            <Tag color="default">Автоматический проход</Tag>
          </ParticipantRow>
        ) : (
          <>
            <ParticipantRow $isWinner={isWinner1}>
              <ParticipantInfo>
                <ParticipantName>{participant1Info.name}</ParticipantName>
                {(participant1Info.club || participant1Info.city) && (
                  <ParticipantDetails>
                    {participant1Info.club}
                    {participant1Info.club && participant1Info.city && ' • '}
                    {participant1Info.city}
                  </ParticipantDetails>
                )}
              </ParticipantInfo>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {(isLive || isCompleted) && match.participant1Score !== undefined && match.participant1Score !== null && (
                  <ScoreDisplay>{match.participant1Score}</ScoreDisplay>
                )}
                {isUserOrganizer && !isCompleted && match.participant1Id && (
                  <WinnerButton
                    type="default"
                    size="small"
                    loading={settingWinner === match.id}
                    onClick={() => handleSetWinner(match, match.participant1Id!)}
                  >
                    Победил
                  </WinnerButton>
                )}
                {isWinner1 && <CheckCircleOutlined style={{ color: '#52c41a' }} />}
              </div>
            </ParticipantRow>

            <div style={{ textAlign: 'center', margin: '8px 0', fontSize: '20px', fontWeight: 'bold' }}>
              VS
            </div>

            <ParticipantRow $isWinner={isWinner2}>
              <ParticipantInfo>
                <ParticipantName>{participant2Info.name}</ParticipantName>
                {(participant2Info.club || participant2Info.city) && (
                  <ParticipantDetails>
                    {participant2Info.club}
                    {participant2Info.club && participant2Info.city && ' • '}
                    {participant2Info.city}
                  </ParticipantDetails>
                )}
              </ParticipantInfo>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {(isLive || isCompleted) && match.participant2Score !== undefined && match.participant2Score !== null && (
                  <ScoreDisplay>{match.participant2Score}</ScoreDisplay>
                )}
                {isUserOrganizer && !isCompleted && match.participant2Id && (
                  <WinnerButton
                    type="default"
                    size="small"
                    loading={settingWinner === match.id}
                    onClick={() => handleSetWinner(match, match.participant2Id!)}
                  >
                    Победил
                  </WinnerButton>
                )}
                {isWinner2 && <CheckCircleOutlined style={{ color: '#52c41a' }} />}
              </div>
            </ParticipantRow>
          </>
        )}

        <MatchInfo>
          <div>
            {match.tatamiNumber && (
              <Tag 
                color="blue"
                style={{ fontSize: '14px', padding: '6px 12px', fontWeight: '500' }}
              >
                Татами {match.tatamiNumber}
              </Tag>
            )}
            {match.round && (
              <Tag 
                style={{ fontSize: '14px', padding: '6px 12px', fontWeight: '500' }}
              >
                Раунд {match.round}
              </Tag>
            )}
          </div>
          {isCompleted && match.updatedAt && (
            <span style={{ color: '#8c8c8c', fontSize: '12px' }}>
              Завершен: {dayjs(match.updatedAt).format('HH:mm:ss')}
            </span>
          )}
        </MatchInfo>
      </MatchCard>
    );
  };

  if (loading && matches.length === 0) {
    return (
      <ScoreboardContainer>
        <Spin size="large" style={{ display: 'block', textAlign: 'center', marginTop: '100px' }} />
      </ScoreboardContainer>
    );
  }

  // Проверяем, может ли пользователь видеть обновления в реальном времени
  if (!canViewUpdates) {
    return (
      <ScoreboardContainer>
        <Empty 
          description="Обновления в реальном времени еще не доступны. Дождитесь публикации сеток менеджером турнира."
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </ScoreboardContainer>
    );
  }

  return (
    <ScoreboardContainer>
      <HeaderSection>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>
            <TrophyOutlined /> Live Табло
          </h1>
          {tournamentId && <p style={{ margin: '8px 0 0 0', color: '#8c8c8c' }}>
            Турнир #{tournamentId}
          </p>}
        </div>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          {availableTatami.length > 0 && (
            <Select
              value={selectedTatami}
              onChange={(value) => setSelectedTatami(value)}
              style={{ minWidth: 150 }}
              placeholder="Выберите татами"
            >
              <Select.Option value="all">Все татами</Select.Option>
              {availableTatami.map(tatami => (
                <Select.Option key={tatami} value={tatami}>
                  Татами {tatami}
                </Select.Option>
              ))}
            </Select>
          )}
          <ConnectionStatus $connected={isConnected}>
            {isConnected ? <WifiOutlined /> : <WifiOffOutlined />}
            {isConnected ? 'Подключено' : 'Отключено'}
          </ConnectionStatus>
          {!isConnected && (
            <ReloadOutlined 
              onClick={reconnect} 
              style={{ cursor: 'pointer', fontSize: '18px' }}
              title="Переподключиться"
            />
          )}
        </div>
      </HeaderSection>

      {error && (
        <Alert
          message="Ошибка"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 24 }}
        />
      )}

      <StatsRow gutter={16}>
        <Col span={12}>
          <Card>
            <Statistic
              title="В процессе"
              value={liveMatches.length}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card>
            <Statistic
              title="Завершенные"
              value={completedMatches.length}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </StatsRow>

      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as 'live' | 'completed')}
        items={[
          {
            key: 'live',
            label: (
              <span>
                <StatusBadge status="processing" text="В процессе" />
                {liveMatches.length > 0 && ` (${liveMatches.length})`}
              </span>
            ),
            children: (
              <MatchesContainer>
                {liveMatches.length > 0 ? (
                  liveMatches.map(renderMatchCard)
                ) : (
                  <Empty description="Нет матчей в процессе" />
                )}
              </MatchesContainer>
            ),
          },
          {
            key: 'completed',
            label: (
              <span>
                <CheckCircleOutlined /> Завершенные
                {completedMatches.length > 0 && ` (${completedMatches.length})`}
              </span>
            ),
            children: (
              <MatchesContainer>
                {completedMatches.length > 0 ? (
                  completedMatches.map(renderMatchCard)
                ) : (
                  <Empty description="Нет завершенных матчей" />
                )}
              </MatchesContainer>
            ),
          },
        ]}
      />
    </ScoreboardContainer>
  );
};

export default LiveScoreboard;


